import { Component, Inject, Optional, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { Subject, takeUntil } from 'rxjs';
import dayjs from 'dayjs';
import {
  ManualTransactionBatchPayload,
  ManualTransactionRowError,
  PaymentProcessingService,
} from '@lmc-app/core/services/payment-processing.service';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';
import { CUSTOM_DATE_FORMAT } from '@lmc-app/shared/constants/common.constant';
import { LmcDialogComponent } from '@lmc-app/shared/components/dialog/lmc-dialog.component';
import {
  ManualBankAccount,
  ManualTransactionPrefill,
  ManualTransactionRowModel,
  achStatusLabel,
  createEmptyRow,
  createPrefilledRow,
} from './manual-transaction-dialog.model';
import {
  CustomBankAccount,
  CustomBankAccountDialogComponent,
  CustomBankAccountDialogData,
} from './custom-bank-account-dialog.component';
import { FUNDING_TYPE_OPTIONS, FundingType } from '../funding-type.constant';
import { KapchargeTransactionFlow } from '@lmc-app/shared/enum/kapcharge-transaction-status.enum';
import {
  TransactionPreviewStatus,
  PREVIEW_STATUS_LABELS,
} from '@lmc-app/shared/enum/transaction-preview-status.enum';

export type ManualTransactionDialogResult = 'submitted' | null;

/**
 * Optional opening state. Passed by the promise-to-pay bridge, which has already run the
 * loan lookup and hands the result straight in; opened with no data, the dialog behaves
 * exactly as before with one empty row.
 */
export interface ManualTransactionDialogData {
  prefill?: ManualTransactionPrefill[];
}

@Component({
  selector: 'app-manual-transaction-dialog',
  templateUrl: './manual-transaction-dialog.component.html',
  styleUrls: ['./manual-transaction-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTableModule,
    MatTooltipModule,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMAT },
  ],
})
export class ManualTransactionDialogComponent implements OnDestroy {
  readonly FormatUtil = FormatUtil;
  readonly fundingTypeOptions = FUNDING_TYPE_OPTIONS;
  /** Whole-batch transaction flow options (Collect = money-in, Pay = money-out / refund). */
  readonly transactionFlowOptions: ReadonlyArray<{ value: KapchargeTransactionFlow; label: string }> = [
    { value: KapchargeTransactionFlow.Collect, label: 'Collect' },
    { value: KapchargeTransactionFlow.Pay, label: 'Pay' },
  ];
  readonly displayedColumns = ['index', 'loanNumber', 'payeeName', 'amount', 'paymentDate', 'bankAccount', 'actions'];
  readonly noteColumns = ['noteIndex', 'note'];
  readonly minPaymentDate = dayjs().startOf('day').toDate();
  /** Value of the dropdown's Custom option; opens the custom account form, never selected. */
  readonly CUSTOM_OPTION = 'custom';

  headerForm = new FormGroup({
    fundingType: new FormControl<string>('', [Validators.required]),
    transactionFlow: new FormControl<KapchargeTransactionFlow>(KapchargeTransactionFlow.Collect, [Validators.required]),
  });

  rows: ManualTransactionRowModel[] = [createEmptyRow()];

  isSubmitting = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<ManualTransactionDialogComponent, ManualTransactionDialogResult>,
    private readonly paymentProcessingService: PaymentProcessingService,
    private readonly dialog: MatDialog,
    @Optional() @Inject(MAT_DIALOG_DATA) data?: ManualTransactionDialogData,
  ) {
    if (data?.prefill?.length) {
      this.rows = data.prefill.map(createPrefilledRow);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackByUid(_index: number, row: ManualTransactionRowModel): string {
    return row.uid;
  }

  addRow(): void {
    this.rows = [...this.rows, createEmptyRow()];
  }

  removeRow(row: ManualTransactionRowModel): void {
    if (this.rows.length === 1) {
      // Reset the last row instead of leaving zero rows
      this.rows = [createEmptyRow()];
      return;
    }
    this.rows = this.rows.filter((r) => r.uid !== row.uid);
  }

  triggerLookupIfReady(row: ManualTransactionRowModel): void {
    const loanNumber = row.loanNumber.trim();
    if (!loanNumber) {
      // Cleared loan number: back to an empty row.
      if (row.lastLookupKey !== null) this.resetRow(row, true);
      return;
    }
    if (row.lookupState === 'loading') return;

    if (loanNumber === row.lastLookupKey) return;

    this.resetRow(row, false);
    row.lastLookupKey = loanNumber;
    row.lookupState = 'loading';

    this.paymentProcessingService
      .lookupLoan(loanNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          // Ignore a response for a loan number that has since been cleared or changed.
          if (row.lastLookupKey !== loanNumber) return;
          row.payeeName = result.payeeName;
          row.bankAccounts = result.bankAccounts ?? [];
          row.lookupState = 'done';
          this.selectBankAccount(row, row.bankAccounts[0] ?? null);
        },
        error: (err) => {
          if (row.lastLookupKey !== loanNumber) return;
          // No loan, or no bank account for the customer — nothing was found to charge.
          row.lookupState = 'done';
          row.status = TransactionPreviewStatus.CustomerNotFound;
          row.error = err?.error?.message ?? err?.message ?? null;
        },
      });
  }

  /** Clears what the lookup filled; `clearInputs` also clears the amount and date. */
  private resetRow(row: ManualTransactionRowModel, clearInputs: boolean): void {
    row.lastLookupKey = null;
    row.lookupState = 'idle';
    row.status = null;
    row.error = null;
    row.rowError = null;
    row.payeeName = '';
    row.institutionCode = '';
    row.transitNumber = '';
    row.accountNumber = '';
    row.bankAccounts = [];
    row.selectedBankAccount = null;
    if (clearInputs) {
      row.amount = null;
      row.paymentDate = null;
    }
  }

  /**
   * Fills the row's bank fields from the chosen account and adopts that account's
   * validation verdict. Only Valid is submittable (see isRowReady).
   */
  selectBankAccount(row: ManualTransactionRowModel, account: ManualBankAccount | null): void {
    row.selectedBankAccount = account;
    row.institutionCode = account?.institutionCode ?? '';
    row.transitNumber = account?.transitNumber ?? '';
    row.accountNumber = account?.accountNumber ?? '';
    row.status = account?.status ?? null;
    row.error = account?.error ?? null;
  }

  /** Dropdown change: the Custom option opens the form and leaves the current selection. */
  onBankAccountChange(
    row: ManualTransactionRowModel,
    value: ManualBankAccount | string,
    select: MatSelect,
  ): void {
    if (value !== this.CUSTOM_OPTION) {
      this.selectBankAccount(row, value as ManualBankAccount);
      return;
    }
    select.writeValue(row.selectedBankAccount);
    this.openCustomBankAccount(row);
  }

  private openCustomBankAccount(row: ManualTransactionRowModel): void {
    this.dialog
      .open<CustomBankAccountDialogComponent, CustomBankAccountDialogData, CustomBankAccount>(
        CustomBankAccountDialogComponent,
        { width: '480px', maxWidth: '90vw', autoFocus: 'first-heading', data: { loanNumber: row.loanNumber.trim(), payeeName: row.payeeName } },
      )
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((entered) => {
        if (!entered) return;
        // Pick the existing entry when the same account is already listed.
        const existing = row.bankAccounts.find((a) =>
          a.institutionCode === entered.institutionCode
          && a.transitNumber === entered.transitNumber
          && a.accountNumber === entered.accountNumber);
        if (existing) {
          this.selectBankAccount(row, existing);
          return;
        }
        // Customer-data verdict is per customer, so it is taken from a listed account.
        const listed = row.bankAccounts.find((a) => !a.custom) ?? row.bankAccounts[0];
        const account: ManualBankAccount = {
          ...entered,
          loanNumber: null,
          achStatus: null,
          status: listed?.status ?? TransactionPreviewStatus.Valid,
          error: listed?.error ?? null,
          custom: true,
        };
        row.bankAccounts = [...row.bankAccounts, account];
        this.selectBankAccount(row, account);
      });
  }

  /** The loan number resolved to a loan; gates the amount, date and bank inputs. */
  hasLoan(row: ManualTransactionRowModel): boolean {
    return row.lookupState === 'done' && row.status !== TransactionPreviewStatus.CustomerNotFound;
  }

  bankPlaceholder(row: ManualTransactionRowModel): string {
    if (row.lookupState === 'loading') return 'Looking up…';
    return this.hasLoan(row) ? 'Select bank account' : 'Enter a valid loan # first';
  }

  bankAccountLabel(account: ManualBankAccount): string {
    const label = `${account.institutionCode}-${account.transitNumber} / ${account.accountNumber}`;
    if (account.custom) return `${label} · Custom`;
    return account.achStatus == null ? label : `${label} · ${achStatusLabel(account.achStatus)}`;
  }

  /** Looked up as anything but Valid, or rejected by the submit call. */
  isRowBlocked(row: ManualTransactionRowModel): boolean {
    return !!row.rowError || (row.lookupState === 'done' && !!row.status && row.status !== TransactionPreviewStatus.Valid);
  }

  /** Status name plus its detail for a blocked row; empty for a valid one. */
  rowNote(row: ManualTransactionRowModel): string {
    if (row.rowError) return row.rowError;
    if (!this.isRowBlocked(row) || !row.status) return '';
    const label = PREVIEW_STATUS_LABELS[row.status];
    return row.error ? `${label}: ${row.error}` : label;
  }

  get readyRowCount(): number {
    return this.rows.filter((r) => this.isRowReady(r)).length;
  }

  get totalAmount(): number {
    return this.rows.reduce((sum, r) => sum + (r.amount ?? 0), 0);
  }

  /** Helper text under the Transaction Flow select clarifying money direction. */
  isRowReady(row: ManualTransactionRowModel): boolean {
    return (
      row.status === TransactionPreviewStatus.Valid &&
      !!row.payeeName &&
      !!row.institutionCode &&
      !!row.transitNumber &&
      !!row.accountNumber &&
      row.amount != null &&
      row.amount > 0 &&
      row.paymentDate != null
    );
  }

  get canSubmit(): boolean {
    return (
      this.headerForm.valid &&
      this.rows.length > 0 &&
      this.rows.every((r) => this.isRowReady(r)) &&
      !this.isSubmitting
    );
  }

  onSubmit(): void {
    if (!this.canSubmit) {
      this.headerForm.markAllAsTouched();
      return;
    }

    this.rows.forEach((r) => (r.rowError = null));
    this.isSubmitting = true;

    const header = this.headerForm.value;
    const payload: ManualTransactionBatchPayload = {
      fundingType: header.fundingType as FundingType,
      transactionFlow: header.transactionFlow!,
      transactions: this.rows.map((r) => ({
        loanNumber: r.loanNumber.trim(),
        payeeName: r.payeeName,
        institutionCode: r.institutionCode,
        transitNumber: r.transitNumber,
        accountNumber: r.accountNumber,
        amount: r.amount!,
        paymentDate: dayjs(r.paymentDate!).format('YYYY-MM-DD'),
        // Present only when the row came from a promise to pay; the backend links the
        // two and moves the promise to Submitted as the batch is written.
        ...(r.promiseToPayId != null ? { promiseToPayId: r.promiseToPayId } : {}),
      })),
    };

    this.paymentProcessingService
      .createManualBatch(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSubmitting = false;
          this.dialogRef.close('submitted');
        },
        error: (err) => {
          this.isSubmitting = false;
          const rowErrors: ManualTransactionRowError[] | undefined = err?.error?.errors;
          if (Array.isArray(rowErrors) && rowErrors.length > 0) {
            rowErrors.forEach((e) => {
              const row = this.rows[e.index];
              if (row) row.rowError = `${e.field}: ${e.message}`;
            });
            this.dialog.open(LmcDialogComponent, {
              data: {
                title: 'Validation Failed',
                content: 'One or more rows failed validation. See inline errors and try again.',
                positiveButtonText: 'OK',
              },
            });
            return;
          }
          this.dialog.open(LmcDialogComponent, {
            data: {
              title: 'Submission Failed',
              content: err?.error?.message ?? err?.message ?? 'Failed to create batch. Please try again.',
              positiveButtonText: 'OK',
            },
          });
        },
      });
  }
}
